import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import {
  Loader2,
  ClipboardList,
  User,
  Calendar,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';

export const AuditLog = () => {
  const { api, language } = useAuth();
  const { t } = useTranslation();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarMap, setCalendarMap] = useState({});

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const [logsRes, calsRes] = await Promise.all([
        api.get('/audit-logs'),
        api.get('/calendars'),
      ]);
      setLogs(logsRes.data);
      const map = {};
      for (const cal of calsRes.data || []) map[cal.id] = cal.name;
      setCalendarMap(map);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error(t('auditLog.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'text-green-400 bg-green-400/10';
      case 'UPDATE': return 'text-blue-400 bg-blue-400/10';
      case 'DELETE': return 'text-red-400 bg-red-400/10';
      case 'DEDUCT': return 'text-yellow-400 bg-yellow-400/10';
      case 'APPLY': return 'text-purple-400 bg-purple-400/10';
      default: return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'BOOKING': return <Calendar className="w-4 h-4" />;
      case 'CLIENT': return <User className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const normalizeKey = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const getActionLabel = (action) => {
    const key = normalizeKey(action);
    const translated = t(`auditLog.actions.${key}`);
    return translated === `auditLog.actions.${key}` ? (action || '') : translated;
  };

  const getEntityLabel = (entityType) => {
    const key = normalizeKey(entityType);
    const translated = t(`auditLog.entities.${key}`);
    return translated === `auditLog.entities.${key}` ? (entityType || '') : translated;
  };

  // Formats an ISO datetime string to dd/MM/yyyy HH:mm, returns raw if it fails.
  const fmtDatetime = (iso) => {
    if (!iso) return '—';
    try { return format(parseISO(iso), 'dd/MM/yyyy HH:mm', { locale }); }
    catch { return iso.slice(0, 16); }
  };

  const resolveCalendar = (id) => calendarMap[id] || id;

  const getDetailsLabel = (log) => {
    const details = log?.details || '';

    // ── Entradas nuevas (ya en español desde server.py) ──────────────────────
    // Pasan directamente — el servidor ya las genera legibles.

    // ── Compat: logs anteriores en inglés ────────────────────────────────────

    // "Created booking for <client> - <service>"
    const mCreated = /^Created booking for (.+?) - (.+)$/.exec(details);
    if (mCreated) {
      return t('auditLog.detailMessages.createdBooking', { client: mCreated[1], service: mCreated[2] });
    }

    // "Deleted booking"
    if (details === 'Deleted booking') {
      return t('auditLog.detailMessages.deletedBooking');
    }

    // "Updated booking: <key>: <val>, ..."  (entradas antiguas)
    const mUpdated = /^Updated booking: (.+)$/.exec(details);
    if (mUpdated) {
      // Parse key:value pairs from the changes string
      const pairs = {};
      mUpdated[1].split(/, (?=\w+: )/).forEach((chunk) => {
        const sep = chunk.indexOf(': ');
        if (sep !== -1) pairs[chunk.slice(0, sep).trim()] = chunk.slice(sep + 2).trim();
      });
      const svcName = pairs.calendarId ? resolveCalendar(pairs.calendarId) : null;
      const dtRaw = pairs.startDateTime || null;
      const dtFmt = dtRaw ? fmtDatetime(dtRaw) : null;
      const STATUS_ES = { ATTENDED: 'Asistido', RESERVED: 'Reservado', NO_SHOW: 'No presentado',
                          LATE_CANCEL: 'Cancelado tarde', CANCELLED: 'Cancelado' };
      const statusLabel = pairs.status ? (STATUS_ES[pairs.status] || pairs.status) : null;

      let label = 'Reserva modificada';
      if (svcName && dtFmt) label += `: ${svcName} — ${dtFmt}`;
      else if (svcName) label += `: ${svcName}`;
      else if (dtFmt) label += `: ${dtFmt}`;
      if (statusLabel) label += ` → ${statusLabel}`;
      return label;
    }

    // "Applied template '<name>' for calendar <uuid> on <date>: N created, M skipped"
    const mApply = /^Applied template '(.+?)' for calendar ([^\s]+) on ([^:]+): (\d+) created, (\d+) skipped/.exec(details);
    if (mApply) {
      const [, tplName, calId, dateStr, created, skipped] = mApply;
      const svcName = resolveCalendar(calId);
      let dateFmt = dateStr.trim();
      try { dateFmt = format(parseISO(dateStr.trim()), 'dd/MM/yyyy', { locale }); } catch {}
      return `Plantilla '${tplName}' aplicada al servicio ${svcName} el ${dateFmt}: ${created} creadas, ${skipped} omitidas`;
    }

    return details;
  };

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ClipboardList className="w-6 h-6 text-zinc-400" />
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          {t('auditLog.title')}
        </h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {t('auditLog.empty')}
        </div>
      ) : (
        <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-220px)] pr-2">
          {logs.map((log) => (
            <div
              key={log.id}
              className="flex items-start gap-4 p-4 bg-card border border-zinc-800 rounded-sm"
              data-testid={`audit-log-${log.id}`}
            >
              <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
                {getEntityIcon(log.entityType)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                    {getActionLabel(log.action)}
                  </span>
                  <span className="text-xs text-zinc-500">{getEntityLabel(log.entityType)}</span>
                </div>
                <div className="text-sm">{getDetailsLabel(log)}</div>
                <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {log.userName}
                  </span>
                  <span>
                    {format(parseISO(log.timestamp), 'dd/MM/yyyy HH:mm', { locale })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
